import { StyleSheet, View } from 'react-native';
import { Icon, type IconName } from '../primitives/Icon';
import { Text, YStack } from 'tamagui';
import { CloudDecoration } from './CloudDecoration';
import { RoadRailDecoration } from './RoadRailDecoration';

export type EmptyStateDecoration = 'cloud' | 'rails' | 'none';

interface EmptyStateProps {
  icon?: IconName | undefined;
  title: string;
  subtitle?: string | undefined;
  /**
   * Optional decorative backdrop per ADR-0080. `'cloud'` for default
   * empty states, `'rails'` for travel-themed empty states (no trips,
   * no journeys), `'none'` (default) keeps the current behaviour.
   */
  decoration?: EmptyStateDecoration | undefined;
}

export function EmptyState({ icon, title, subtitle, decoration = 'none' }: EmptyStateProps) {
  const content = (
    <YStack flex={1} justifyContent="center" alignItems="center" minHeight={160} gap="$2" padding="$4">
      {icon ? <Icon name={icon} size={36} color="$textSubdued" /> : null}
      <Text color="$textSubdued" fontSize="$3" fontWeight="600">{title}</Text>
      {subtitle ? <Text color="$textSubdued" fontSize="$2">{subtitle}</Text> : null}
    </YStack>
  );

  if (decoration === 'none') return content;

  return (
    <View style={styles.frame}>
      <View style={styles.decorationLayer} pointerEvents="none">
        {decoration === 'cloud' ? <CloudDecoration /> : null}
        {decoration === 'rails' ? <RoadRailDecoration /> : null}
      </View>
      {content}
    </View>
  );
}

// Decoration sits behind the content; flex container preserves the
// existing centered layout.
const styles = StyleSheet.create({
  frame: { flex: 1, position: 'relative' },
  decorationLayer: { ...StyleSheet.absoluteFill },
});
